/**
 * API 服务层
 * 封装与后端 API 的通信，提供类型安全的数据获取函数
 */
import type { ApiResponse, ShareResponse } from '@photo-process/shared';

/** API 基础地址，支持环境变量配置 */
const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? 'https://photo-process-api.wangdake.de/api';

/** API 错误类型，区分不同的错误场景 */
export type ApiErrorType = 'NOT_FOUND' | 'NETWORK' | 'SERVER';

/** API 请求错误，包含错误类型和用户友好提示 */
export class ApiError extends Error {
  readonly type: ApiErrorType;

  constructor(message: string, type: ApiErrorType) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
  }
}

/**
 * 根据令牌获取分享数据
 * 公开接口，无需认证
 *
 * @param token - 分享令牌
 * @returns 分享响应数据（含照片列表和预签名 URL）
 * @throws {ApiError} 网络错误、404 或服务器错误
 */
export async function fetchShareByToken(token: string): Promise<ShareResponse> {
  let response: Response;

  try {
    response = await fetch(`${API_BASE_URL}/shares/${token}`);
  } catch {
    throw new ApiError('网络连接失败，请检查网络后重试', 'NETWORK');
  }

  if (response.status === 404) {
    throw new ApiError('分享不存在或已被删除', 'NOT_FOUND');
  }

  if (!response.ok) {
    throw new ApiError('服务器错误，请稍后重试', 'SERVER');
  }

  let json: ApiResponse<ShareResponse>;

  try {
    json = (await response.json()) as ApiResponse<ShareResponse>;
  } catch {
    throw new ApiError('响应数据解析失败', 'SERVER');
  }

  if (!json.success) {
    throw new ApiError(json.error.message, 'SERVER');
  }

  return json.data;
}
